import React, { useState, useEffect } from 'react';
import { Stack, Button, Paper, Typography, Chip, TablePagination, Tooltip } from '@mui/material';
import TrimTables from 'Components/Reusable/TrimTables/TrimTables';
import { Add } from '@mui/icons-material';
import { ColumnType } from 'Components/Common/Tables/TableHeaders';
import { depositSlipHead } from 'Components/DepositSlip/Mock';
import CustomTable from 'Components/Common/Tables/CustomTable/CustomTable';
import { useNavigate } from 'react-router-dom';
import useDepositSlipAction, { DepositSlips } from 'hooks/useDepositSlipAction';
import { StateType, OrderType, FilterPayload } from 'contexts/intialstates/Types';
import { convertDate } from 'helpers/dayAndTime';
import { pagination } from 'Components/Common/Tables/Tables';
import OrderTypeArrow from 'Components/Common/Tables/OrderTypeArrow';
import { sortTable } from 'Components/Common/Tables/sortTable';
import TableFiltering from 'Components/Reusable/TableFiltering/TableFiltering';
import FilterListButton from 'Components/Reusable/FilterList/FilterList';
import useUserActions from 'hooks/useUserActions';

const DepositSlipList = () => {
	const { handleRoles } = useUserActions();
	const navigate = useNavigate();
	const { getDepositSlips } = useDepositSlipAction();
	const [trimList, setTrimList] = useState<ColumnType[]>([]);
	const [depositSlips, setDepositSlips] = useState<StateType<DepositSlips>>({
		data: null,
		loading: false,
		error: null,
	});
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [orderType, setOrderType] = useState<OrderType>('asc');
	const [label, setLabel] = useState<string>('');
	const [orderBy, setOderBy] = useState<string>('');
	const [reload, setReload] = useState(false);
	const [filterPayload, setFilterPayload] = useState<Array<FilterPayload>>([]);

	useEffect(() => {
		getDepositSlips({ depositSlips, setDepositSlips, page, pageSize, orderBy, filterPayload });
	}, [orderBy, page, pageSize, reload]);

	const handleChangePage = (event: unknown, value: number) => {
		setPage(value);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPageSize(+event.target.value);
		setPage(0);
	};

	const getTableData = () => {
		return depositSlips?.data?.body.length
			? depositSlips.data.body.map((row: any, index: number) => {
					return {
						's/n': index + 1,
						id: row.id,
						society: row.society ? row.society.code : 'N/A',
						totalAmount: row.totalAmount || 'N/A',
						bankDepositDate: row?.bankDepositDate ? convertDate(row?.bankDepositDate) : 'N/A',
						status: <Chip label={row.status} color="primary" variant="outlined" />,
						createdBy: row?.createdBy?.name || 'N/A',
						updatedBy: row?.updatedBy?.name || 'N/A',
						created: row?.created ? `${convertDate(row?.created)}` : 'N/A',
						updated: row?.updated ? `${convertDate(row?.updated)}` : 'N/A',
					};
			  })
			: [];
	};

	const getColumnData = () => {
		return trimList.map((column) => {
			return {
				...column,
				data: (
					<>
						<Tooltip title={column.id == 'actions' ? '' : `Sort by ${column.label}`}>
							<span
								style={{
									cursor: 'pointer',
									margin: '10px',
								}}
								onClick={() => {
									sortTable({
										column: column.id,
										setLabel,
										setOderBy,
										setOrderType,
										orderType,
										label,
									});
								}}
							>
								{column.label}
								{column.id == label && <OrderTypeArrow orderType={orderType} />}
							</span>
						</Tooltip>
						<TableFiltering
							field={column.id}
							filterType={column.filterType}
							list={column.list}
							reload={reload}
							setIsReload={setReload}
							setFilterPayload={setFilterPayload}
							filterPayload={filterPayload}
							setPage={setPage}
							page={page}
						/>
					</>
				),
			};
		});
	};

	const handleDrag = (reOrderedData: ColumnType[]) => {
		setTrimList(reOrderedData);
	};

	const handleResize = (resizedData: ColumnType[]) => {
		setTrimList(resizedData);
	};

	const handleRowClick = (row: any) => {
		navigate(`/bookkeeping/deposit-slip/${row.id}`);
	};

	return (
		<Stack>
			<Stack direction={'row'} justifyContent={'space-between'} mb={3}>
				<Stack direction={'column'}>
					<Typography variant="h5" sx={{ color: '#1976D2' }}>
						Deposit Slip
					</Typography>
					<Stack>Create and manage deposit slips here.</Stack>
				</Stack>
				<Stack direction={'row'} alignItems={'center'}>
					<TrimTables
						name={'depositSlipHead'}
						list={depositSlipHead}
						trimList={trimList}
						setTrimList={setTrimList}
					/>
					{handleRoles('bookkeeping-actions') && (
						<Button
							onClick={() => navigate('/bookkeeping/deposit-slip/create')}
							size={'small'}
							startIcon={<Add />}
							variant="contained"
						>
							Add Donation Slip
						</Button>
					)}
					<FilterListButton
						setPage={setPage}
						page={page}
						setIsReload={setReload}
						reload={reload}
						setFilterPayload={setFilterPayload}
						filterPayload={filterPayload}
					/>
				</Stack>
			</Stack>
			<Stack>
				<Paper sx={{ width: '100%', overflow: 'hidden' }}>
					<CustomTable
						loading={depositSlips?.loading}
						columnData={getColumnData()}
						tableData={getTableData()}
						handleDrag={handleDrag}
						handleResize={handleResize}
						emptyMessage={'No deposit slips available'}
						handleRowClick={handleRowClick}
					/>
					<TablePagination
						rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
						component="div"
						count={(depositSlips?.data?.totalRecords as number) || 0}
						rowsPerPage={pageSize}
						page={page}
						sx={pagination}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</Paper>
			</Stack>
		</Stack>
	);
};

export default DepositSlipList;
